import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { BODY_VIEW_OPTIONS, FREE_POINT_LABEL, getRegionsForView } from '../../components/bodySilhouette/bodyRegions';
import { INTENSITY_OPTIONS, MARKER_TYPES, hasIntensity, markerTitle } from '../../pages/appointment/components/bodyMap/bodyMapMarkers';
import styles from './BodyMapMarkerPopup.module.css';

const MAX_NOTE_LENGTH = 500;

const viewLabel = (view) => (BODY_VIEW_OPTIONS.find((item) => item.key === view) || {}).label || view;

/**
 * Edição de um marcador do mapa corporal: tipo, intensidade, região e nota.
 * Trabalha em rascunho; só "Salvar" devolve as alterações (`onSave(patch)`),
 * "Remover" pede confirmação e chama `onRemove()`. Fechar descarta.
 */
function BodyMapMarkerPopup({ marker, number, onSave, onRemove, OnHide }) {
  const regions = getRegionsForView(marker.view);
  const [draft, setDraft] = useState({
    type: marker.type,
    intensity: hasIntensity(marker) ? marker.intensity : '',
    region: marker.region || '',
    note: marker.note || '',
  });
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  function saveHandler() {
    const region = regions.find((item) => item.key === draft.region) || null;
    onSave({
      type: draft.type,
      intensity: draft.intensity === '' ? null : Number(draft.intensity),
      region: region ? region.key : '',
      side: region ? region.side : null,
      note: draft.note.trim(),
    });
    OnHide();
  }

  function removeHandler() {
    onRemove();
    OnHide();
  }

  const content = (
    <div className={styles.container}>
      <span className={styles.context}>
        {markerTitle(marker)} · {viewLabel(marker.view)}
      </span>

      <div className={styles.row}>
        <FormControl size="small" fullWidth>
          <InputLabel id="body-map-popup-type">Tipo</InputLabel>
          <Select
            labelId="body-map-popup-type"
            label="Tipo"
            value={draft.type}
            onChange={(event) => update({ type: event.target.value })}
          >
            {MARKER_TYPES.map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel id="body-map-popup-intensity">Intensidade</InputLabel>
          <Select
            labelId="body-map-popup-intensity"
            label="Intensidade"
            value={draft.intensity}
            onChange={(event) => update({ intensity: event.target.value })}
          >
            <MenuItem value="">Sem intensidade</MenuItem>
            {INTENSITY_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>{value} de 10</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <FormControl size="small" fullWidth>
        <InputLabel id="body-map-popup-region">Região</InputLabel>
        <Select
          labelId="body-map-popup-region"
          label="Região"
          value={draft.region}
          onChange={(event) => update({ region: event.target.value })}
        >
          <MenuItem value="">{FREE_POINT_LABEL}</MenuItem>
          {regions.map((item) => (
            <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="Nota"
        value={draft.note}
        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
        onChange={(event) => update({ note: event.target.value })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton
        variant="outlined"
        style="error"
        label="Remover"
        icon={<IconDelete />}
        onClick={() => setShowConfirmRemove(true)}
      />
      <div className={styles.footerRight}>
        <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={() => OnHide()} />
        <CustomButton variant="contained" style="primary" label="Salvar" icon={<IconSave />} onClick={saveHandler} />
      </div>

      {showConfirmRemove && (
        <ConfirmPopup
          Visibility={showConfirmRemove}
          Title="Remover marcador"
          Description={`Remover o marcador ${number} (${markerTitle(marker)}) deste atendimento?`}
          ConfirmLabel="Remover"
          OnConfirm={removeHandler}
          OnHide={() => setShowConfirmRemove(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={`Marcador ${number}`}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide()}
      maxWidth="sm"
    />
  );
}

export default BodyMapMarkerPopup;
